import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface FinancialSummaryData {
  registrationFees: number;
  accommodationFees: {
    apartments: number;
    dormsFireside: number;
    cottages: number;
    privateCottages: number;
    campingSites: number;
    orphaned: number;
    total: number;
  };
  fridgeRentals: number;
  meals: number;
  creditCardFees: number;
  otherFees: number;
  donations: number;
  totalIncome: number;
  payments: number;
  outstandingBalance: number;
  total: number;
}

interface CottageDiscrepancy {
  id: string;
  name: string;
  placeName: string;
  priceCode: string;
  bucket: 'Cottages' | 'Private Cottages';
  status: string;
  invoiceNumber: string;
  assignedAccommodation: string;
  storedFee: number;
  overrideAmount: number;
  overrideEnabled: boolean;
  oldAmount: number;
  newAmount: number;
  reasons: string[];
}

const cottageBucket = (priceCode: string): CottageDiscrepancy['bucket'] | null => {
  const prefix = priceCode ? priceCode.charAt(0).toUpperCase() : '';
  if (prefix === 'C') return 'Cottages';
  if (prefix === 'P') return 'Private Cottages';
  return null;
};

const roundMoney = (amount: number): number => Math.round(amount * 100) / 100;

// Temporary copy of the pre-August 1 stored-fee Treasurer report.
// Kept so the August 3 print can be reproduced against current Firestore data.
const TempFinancialSummary: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<FinancialSummaryData>({
    registrationFees: 0,
    accommodationFees: {
      apartments: 0,
      dormsFireside: 0,
      cottages: 0,
      privateCottages: 0,
      campingSites: 0,
      orphaned: 0,
      total: 0,
    },
    fridgeRentals: 0,
    meals: 0,
    creditCardFees: 0,
    otherFees: 0,
    donations: 0,
    totalIncome: 0,
    payments: 0,
    outstandingBalance: 0,
    total: 0,
  });
  const [discrepancies, setDiscrepancies] = useState<CottageDiscrepancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [registrationsSnapshot, settingsSnap] = await Promise.all([
          getDocs(collection(db, 'Registrations')),
          getDoc(doc(db, 'settings', 'camp')),
        ]);
        const settingsData = settingsSnap.exists() ? settingsSnap.data() : null;
        const cottageRows: CottageDiscrepancy[] = [];

        let totalRegistrationFees = 0;
        let totalApartments = 0;
        let totalDormsFireside = 0;
        let totalCottages = 0;
        let totalPrivateCottages = 0;
        let totalCampingSites = 0;
        let totalOrphanedAccommodations = 0;
        let totalFridgeRentals = 0;
        let totalMeals = 0;
        let totalCreditCardFees = 0;
        let totalOtherFees = 0;
        let totalDonations = 0;
        let totalPayments = 0;
        let totalOutstandingBalance = 0;

        registrationsSnapshot.forEach((docSnap) => {
          const record = docSnap.data();

          const status = record.status?.toLowerCase();
          if (status === 'pending') {
            return;
          }

          let registrationFeeTotal = 0;
          if (
            record.financial?.registrationFees?.totalRegistrationFees &&
            typeof record.financial.registrationFees.totalRegistrationFees === 'number'
          ) {
            registrationFeeTotal = record.financial.registrationFees.totalRegistrationFees;
          }
          totalRegistrationFees += registrationFeeTotal;

          let accommodationFee = 0;
          const accommodationData = record.financial?.accommodationFees;
          if (accommodationData && accommodationData.accommodationFee && typeof accommodationData.accommodationFee === 'number') {
            accommodationFee = accommodationData.accommodationFee;

            const priceCode = accommodationData.priceCode;
            if (priceCode) {
              const prefix = priceCode.charAt(0).toUpperCase();
              if (prefix === 'A') {
                totalApartments += accommodationFee;
              } else if (prefix === 'C') {
                totalCottages += accommodationFee;
              } else if (prefix === 'D' || prefix === 'F') {
                totalDormsFireside += accommodationFee;
              } else if (prefix === 'P') {
                totalPrivateCottages += accommodationFee;
              } else if (prefix === 'T') {
                totalCampingSites += accommodationFee;
              } else {
                totalOrphanedAccommodations += accommodationFee;
              }
            } else {
              totalOrphanedAccommodations += accommodationFee;
            }
          }

          let otherFeesTotal = 0;
          const otherFeesData = record.financial?.otherFees || {};

          if (otherFeesData.fridge?.cost && typeof otherFeesData.fridge.cost === 'number') {
            totalFridgeRentals += otherFeesData.fridge.cost;
            otherFeesTotal += otherFeesData.fridge.cost;
          }

          if (otherFeesData.donations?.amount && typeof otherFeesData.donations.amount === 'number') {
            totalDonations += otherFeesData.donations.amount;
            otherFeesTotal += otherFeesData.donations.amount;
          }

          if (otherFeesData.meals?.totalMealCost && typeof otherFeesData.meals.totalMealCost === 'number') {
            totalMeals += otherFeesData.meals.totalMealCost;
            otherFeesTotal += otherFeesData.meals.totalMealCost;
          }

          const recordTotalFees = registrationFeeTotal + accommodationFee + otherFeesTotal;

          const paymentsArray = record.paymentHistory || record.payments || [];
          if (Array.isArray(paymentsArray) && paymentsArray.length > 0) {
            const recordPayments = paymentsArray.reduce(
              (sum: number, payment: any) => sum + (payment.amount || 0),
              0
            );
            totalPayments += recordPayments;

            const recordCCFees = paymentsArray.reduce(
              (sum: number, payment: any) => sum + (payment.convenienceFee || 0),
              0
            );
            totalCreditCardFees += recordCCFees;

            totalOutstandingBalance += recordTotalFees - recordPayments;
          } else {
            totalOutstandingBalance += recordTotalFees;
          }

          const priceCode = String(accommodationData?.priceCode || '');
          const bucket = cottageBucket(priceCode);
          if (bucket && typeof accommodationFee === 'number' && accommodationFee) {
            const invoiceNumber =
              record.financial?.invoiceNumber ||
              record.financialRecord?.invoiceNumber ||
              record.invoiceNumber ||
              record.invoice?.invoiceNumber ||
              '';
            const assignedAccommodation = String(
              record.financial?.accommodationFees?.assignedAccommodation || ''
            );
            const override = record.financial?.overrides?.accommodation;
            const overrideEnabled = Boolean(override?.enabled);
            const overrideAmount = overrideEnabled ? Number(override?.amount) || 0 : 0;

            const inOldReport = status !== 'pending';
            const inNewReport =
              record.status === 'Assigned' &&
              Boolean(invoiceNumber) &&
              Boolean(assignedAccommodation) &&
              Boolean(settingsData?.rates?.accommodation);

            const oldAmount = inOldReport ? accommodationFee : 0;
            const newAmount = inNewReport ? roundMoney(accommodationFee + overrideAmount) : 0;

            if (roundMoney(oldAmount - newAmount) !== 0) {
              const reasons: string[] = [];
              if (record.status !== 'Assigned') {
                reasons.push(`Status is "${record.status || 'blank'}", not Assigned`);
              }
              if (!invoiceNumber) {
                reasons.push('No invoice number');
              }
              if (!assignedAccommodation) {
                reasons.push('Cottage fee is stored, but no assigned accommodation');
              }
              if (overrideEnabled && overrideAmount !== 0) {
                reasons.push(
                  `Accommodation override ${overrideAmount > 0 ? '+' : ''}${overrideAmount.toFixed(2)}`
                );
              }
              if (reasons.length === 0 && oldAmount !== newAmount) {
                reasons.push('Old and new formulas produced different cottage amounts');
              }

              cottageRows.push({
                id: docSnap.id,
                name:
                  record.registrationInfo?.primaryCamperName ||
                  `${record.firstName || ''} ${record.lastName || ''}`.trim() ||
                  'Unknown',
                placeName:
                  record.financial?.accommodationFees?.placeName ||
                  record.assignedAccommodation?.placeName ||
                  assignedAccommodation ||
                  '',
                priceCode,
                bucket,
                status: record.status || '',
                invoiceNumber: invoiceNumber || '',
                assignedAccommodation,
                storedFee: accommodationFee,
                overrideAmount,
                overrideEnabled,
                oldAmount,
                newAmount,
                reasons,
              });
            }
          }
        });

        const totalAccommodationFees =
          totalApartments +
          totalDormsFireside +
          totalCottages +
          totalPrivateCottages +
          totalCampingSites +
          totalOrphanedAccommodations;
        const totalIncome =
          totalRegistrationFees +
          totalAccommodationFees +
          totalFridgeRentals +
          totalMeals +
          totalCreditCardFees +
          totalOtherFees +
          totalDonations;
        const total = totalPayments + totalOutstandingBalance;

        setData({
          registrationFees: totalRegistrationFees,
          accommodationFees: {
            apartments: totalApartments,
            dormsFireside: totalDormsFireside,
            cottages: totalCottages,
            privateCottages: totalPrivateCottages,
            campingSites: totalCampingSites,
            orphaned: totalOrphanedAccommodations,
            total: totalAccommodationFees,
          },
          fridgeRentals: totalFridgeRentals,
          meals: totalMeals,
          creditCardFees: totalCreditCardFees,
          otherFees: totalOtherFees,
          donations: totalDonations,
          totalIncome,
          payments: totalPayments,
          outstandingBalance: totalOutstandingBalance,
          total,
        });
        cottageRows.sort((a, b) => Math.abs(b.oldAmount - b.newAmount) - Math.abs(a.oldAmount - a.newAmount));
        setDiscrepancies(cottageRows);
      } catch (err) {
        setError('Failed to load financial summary data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const currentDate = new Date().toLocaleDateString();

  const formatMoney = (amount: number) =>
    amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="annual-stupid-report">
      <div style={{ position: 'relative', marginBottom: '8px' }}>
        <h1 style={{ margin: 0, textAlign: 'center' }}>Temp Financial Summary</h1>
        <button
          onClick={() => window.print()}
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0056b3')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#007bff')}
        >
          🖨️ Print Report
        </button>
      </div>
      <p style={{ textAlign: 'center', marginBottom: '8px' }}>As of {currentDate}</p>
      <p style={{ textAlign: 'center', marginBottom: '32px', color: '#666', fontSize: '13px' }}>
        Old stored-fee method — temporary comparison copy
      </p>

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : (
        <div style={{ maxWidth: '800px', margin: '0 auto', fontFamily: 'monospace' }}>
          <h2 style={{ borderBottom: '2px solid #333', paddingBottom: '8px' }}>Income</h2>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Registration Fees</span>
              <span>${formatMoney(data.registrationFees)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Accommodation Fees:</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Apartments</span>
              <span style={{ marginRight: '200px' }}>${formatMoney(data.accommodationFees.apartments)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Dorms/Fireside</span>
              <span style={{ marginRight: '200px' }}>${formatMoney(data.accommodationFees.dormsFireside)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Cottages</span>
              <span style={{ marginRight: '200px' }}>${formatMoney(data.accommodationFees.cottages)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Private Cottages</span>
              <span style={{ marginRight: '200px' }}>${formatMoney(data.accommodationFees.privateCottages)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Camping Sites</span>
              <span style={{ marginRight: '200px', textDecoration: 'underline' }}>
                ${formatMoney(data.accommodationFees.campingSites)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Total Accommodation Fees</span>
              <span>${formatMoney(data.accommodationFees.total)}</span>
            </div>
          </div>

          {data.accommodationFees.orphaned > 0 && (
            <div style={{ marginBottom: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                <span style={{ paddingLeft: '20px' }}>Orphaned Accommodations</span>
                <span>${formatMoney(data.accommodationFees.orphaned)}</span>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Fridge Rentals</span>
              <span>${formatMoney(data.fridgeRentals)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Meals</span>
              <span>${formatMoney(data.meals)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Credit Card Fees</span>
              <span>${formatMoney(data.creditCardFees)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Other Fees</span>
              <span>${formatMoney(data.otherFees)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Donations</span>
              <span>${formatMoney(data.donations)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px', fontWeight: 'bold' }}>Total Income</span>
              <span style={{ fontWeight: 'bold' }}>${formatMoney(data.totalIncome)}</span>
            </div>
          </div>

          <h2 style={{ borderBottom: '2px solid #333', paddingBottom: '8px' }}>Status</h2>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Payments</span>
              <span>${formatMoney(data.payments)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Outstanding Balance</span>
              <span>${formatMoney(data.outstandingBalance)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px', fontWeight: 'bold' }}>Total</span>
              <span style={{ fontWeight: 'bold' }}>${formatMoney(data.total)}</span>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div style={{ maxWidth: '1100px', margin: '48px auto 0', fontFamily: 'sans-serif' }}>
          <h2 style={{ borderBottom: '2px solid #333', paddingBottom: '8px' }}>
            Cottage discrepancies (old vs new)
          </h2>
          <p style={{ color: '#555', marginBottom: '16px' }}>
            These cottage / private cottage accounts are in the temp report at a different amount
            than the current Treasurer&apos;s report. Click a row to open the registration.
          </p>
          {discrepancies.length === 0 ? (
            <p>No cottage fee differences found on the current data.</p>
          ) : (
            <>
              <p style={{ marginBottom: '12px' }}>
                <strong>{discrepancies.length}</strong> account
                {discrepancies.length === 1 ? '' : 's'} differ, totaling{' '}
                <strong>
                  ${formatMoney(discrepancies.reduce((sum, row) => sum + (row.oldAmount - row.newAmount), 0))}
                </strong>{' '}
                more in the old report.
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid #333' }}>
                    <th style={{ padding: '6px' }}>Name</th>
                    <th style={{ padding: '6px' }}>Place</th>
                    <th style={{ padding: '6px' }}>Bucket</th>
                    <th style={{ padding: '6px' }}>Status</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>Old</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>New</th>
                    <th style={{ padding: '6px' }}>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {discrepancies.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => navigate(`/admin/registration-details/${row.id}`)}
                      style={{ borderBottom: '1px solid #ddd', cursor: 'pointer' }}
                    >
                      <td style={{ padding: '6px' }}>{row.name}</td>
                      <td style={{ padding: '6px' }}>
                        {row.placeName || row.assignedAccommodation || row.priceCode}
                      </td>
                      <td style={{ padding: '6px' }}>{row.bucket}</td>
                      <td style={{ padding: '6px' }}>{row.status || '—'}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>${formatMoney(row.oldAmount)}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>${formatMoney(row.newAmount)}</td>
                      <td style={{ padding: '6px' }}>{row.reasons.join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TempFinancialSummary;
